import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME } from "@/config";
import { PROVIDER_NAMES, takeSocialPurpose } from "@/lib/social";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { AuthShell, homeFor } from "@/pages/Login";
import { takeReturn } from "@/lib/returnTo";

// Back from Google or GitHub (backend/social.py): finish a sign-in, or link the provider to the signed-in account.
// A first sign-in whose email an account uses asks for that account's password once, to link them.
export default function SocialCallback() {
  const { provider } = useParams();
  const { setSession, refresh } = useAuth();
  const navigate = useNavigate();
  const name = PROVIDER_NAMES[provider] || provider;
  const [step, setStep] = useState({ phase: "working" });  // working, link (password needed) or error
  const [password, setPassword] = useState("");
  const [linking, setLinking] = useState(false);
  const ran = useRef(false);

  const signedIn = (data) => {
    setSession(data);
    navigate(takeReturn(homeFor(data.user.role)), { replace: true });
    toast.success(`Welcome, ${data.user.name}`);
  };

  useEffect(() => {
    if (ran.current) return;  // once: the code works once
    ran.current = true;
    const query = new URLSearchParams(window.location.search);
    window.history.replaceState(null, "", window.location.pathname);  // the code leaves the address bar
    const purpose = takeSocialPurpose(provider);
    if (!query.get("code") || !query.get("state")) {
      setStep({ phase: "error", purpose, message: query.get("error") === "access_denied"
        ? `You cancelled signing in with ${name}.` : `${name} didn't send you back with a sign-in. Try again.` });
      return;
    }
    const body = { state: query.get("state"), code: query.get("code") };
    (async () => {
      try {
        if (purpose === "link") {
          await api.post(`/auth/social/${provider}/link/finish`, body);
          toast.success(`${name} linked: you can sign in with it now`);
          await refresh();
          navigate("/account", { replace: true });
          return;
        }
        const { data } = await api.post(`/auth/social/${provider}/finish`, body);
        if (data.link) setStep({ phase: "link", ...data.link });
        else signedIn(data);
      } catch (e) {
        setStep({ phase: "error", purpose, message: apiErr(e) });
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const link = async (e) => {
    e.preventDefault();
    setLinking(true);
    try {
      signedIn((await api.post("/auth/social/link", { token: step.token, password })).data);
    } catch (err) {
      toast.error(apiErr(err));
      if (err?.response?.status === 400) setStep({ phase: "error", message: apiErr(err) });
    } finally {
      setLinking(false);
    }
  };

  if (step.phase === "link") return (
    <AuthShell title={`Link ${name} to your account`} subtitle={`${step.email} has a ${APP_NAME} account.`}>
      <form onSubmit={link} className="space-y-4" data-testid="social-link-form">
        <p className="text-sm text-gr-ink-secondary leading-relaxed">
          Type this account's password once. From then on, {name} signs you in to it. You'll get an email about it.
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="social-link-password">Password</Label>
          <Input id="social-link-password" type="password" autoComplete="current-password" value={password}
            onChange={(e) => setPassword(e.target.value)} data-testid="social-link-password" />
        </div>
        <Button type="submit" className="w-full" disabled={!password || linking} data-testid="social-link-submit">
          {linking && <Loader2 className="w-4 h-4 animate-spin" />} Link and sign in
        </Button>
        <Link to="/login" className="block text-center text-sm text-muted-foreground hover:text-foreground">Cancel</Link>
      </form>
    </AuthShell>
  );
  if (step.phase === "error") return (
    <AuthShell title={`Signing in with ${name} didn't work`} subtitle={step.message}>
      <Button asChild className="w-full" data-testid="social-error-back">
        <Link to={step.purpose === "link" ? "/account" : "/login"}>{step.purpose === "link" ? "Back to your account" : "Back to sign in"}</Link>
      </Button>
    </AuthShell>
  );
  return (
    <AuthShell title={`Signing in with ${name}`} subtitle="One moment…">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" data-testid="social-working" />
    </AuthShell>
  );
}
